import React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import EkonomiskAnalysPeriods from '../EkonomiskAnalysPeriods/EkonomiskAnalysPeriods';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import {
  DateInputType,
  ekonomiskAnalysDatesToDateString,
  getDatePlaceholder,
} from '../../../../../../../Utils/dateUtils';
import {
  IEkonomiskAnalysDates,
  IDate,
} from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { CommonPropsAndTypes } from '../../../../../../../BaseComponents/_Types/StandardComponentProps';

interface IProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  dateInputType?: DateInputType;
  isInterval: boolean;
  dates: IEkonomiskAnalysDates;
  lightVersion?: boolean;
  periodLocked?: boolean;
  simple?: boolean;
  onFromDateChange: (date: IDate, changeBoth?: boolean) => void;
  onToDateChange: (date: IDate) => void;
  accessibilityMode: boolean;
  dense?: boolean;
  marginRight?: boolean;
  // Text som visas under periodkomponenten
  headerText?: string;
  padding?: CommonPropsAndTypes.StandardSpacingType;
  fromDateRef?: React.MutableRefObject<HTMLInputElement | null>;
  toDateRef?: React.MutableRefObject<HTMLInputElement | null>;
  isChanges?: boolean;
  updateReport?: () => void;
  classes?: any;
}

const styles = (theme: Theme) => ({
  periodsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: 40,
    marginRight: theme.spacing(5),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },
  periodsInactiveColor: {
    color: theme.palette.grey[400],
  },
  periodsActiveColor: {
    color: theme.customProperties.Fade.LightOnDark.highEmphasis,
  },
  cursor: {
    cursor: 'pointer',
  },
  menuText: {
    color: 'inherit',
  },
  lockIcon: {
    display: 'inline-block',
    position: 'relative',
    top: -3,
    marginLeft: 6,
  },
  lockIconWarning: {
    display: 'inline-block',
    position: 'relative',
    top: -3,
    marginLeft: 6,
    color: theme.customProperties.Warning.dark,
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  dateError: {
    color: theme.palette.error.main,
  },
  periodText: {
    display: 'flex',
    flexDirection: 'column',
  },
  helperText: {
    paddingRight: theme.spacing(2),
  },
  editModePeriods: {},
  clickableObject: {
    '& strong:first-of-type': {
      color: theme.palette.primary.main,
    },
    '&:hover strong:first-of-type, &.accessibility strong:first-of-type': {
      borderBottom: '2px solid ' + theme.palette.primary.main,
      marginBottom: -2,
    },
  },
});

/**
 * En validator som visar en dialog med information när ett felaktigt år angets för den nuvarande kodplanen
 */
export class EkonomiskAnalysPeriodComponent extends React.Component<IProps> {
  componentDidUpdate(prevProps: IProps) {
    if (prevProps.isChanges && !this.props.isChanges) {
      this.props.setIsEditing(false);
    }
  }

  toggleEditing = () => {
    this.props.setIsEditing(!this.props.isEditing);
  };

  render() {
    const {
      cursor,
      menuText,
      periodsContainer,
      lockIcon,
      lockIconWarning,
      periodsInactiveColor,
      periodsActiveColor,
      marginTop,
      dateError,
    } = this.props.classes;

    const placeholder = getDatePlaceholder(this.props.dateInputType);
    const toDateInvalid =
      this.props.dates.to.error ||
      (this.props.dates.to.userString === '' && this.props.dates.from.error);
    return (
      <div
        className={classNames(
          periodsContainer,
          this.props.lightVersion
            ? this.props.isEditing
              ? periodsActiveColor
              : periodsInactiveColor
            : undefined,
          marginTop
        )}
      >
        {!this.props.isEditing && (
          <div className={classNames(cursor, menuText)}>
            <EDTypography
              onClick={this.toggleEditing}
              component={'span'}
              color={'inherit'}
              style={{ display: 'inline' }}
            >
              <div
                style={{ display: 'flex' }}
                className={classNames(this.props.classes.clickableObject, {
                  ['accessibility']: this.props.accessibilityMode,
                })}
              >
                <EDTooltip title="Ange period">
                  <div className={this.props.classes.periodText}>
                    <span>
                      <strong>Period:</strong>
                      &nbsp;
                      <span
                        className={
                          this.props.dates.from.error ? dateError : undefined
                        }
                      >
                        {this.props.dates.from.error
                          ? placeholder
                          : ekonomiskAnalysDatesToDateString(
                              this.props.dates,
                              this.props.dateInputType
                            )}
                      </span>
                    </span>
                    {this.props.isInterval && (
                      <span>
                        <EDIcon
                          color="inherit"
                          iconType="arrow_right_alt"
                          marginLeft={3.25}
                        />
                        <span className={toDateInvalid ? dateError : undefined}>
                          {toDateInvalid
                            ? placeholder
                            : ekonomiskAnalysDatesToDateString(
                                this.props.dates,
                                this.props.dateInputType,
                                true
                              )}
                        </span>
                      </span>
                    )}
                  </div>
                </EDTooltip>
                {this.props.periodLocked !== undefined && (
                  <>
                    {this.props.periodLocked && (
                      <EDTooltip title="Perioden är låst, siffrorna kommer inte ändras.">
                        <span className={lockIcon}>
                          <EDIcon
                            iconType="lock"
                            size="medium"
                            color="inherit"
                          />
                        </span>
                      </EDTooltip>
                    )}
                    {!this.props.periodLocked && (
                      <EDTooltip title="Perioden är inte låst, ändringar kan fortfarande göras.">
                        <span className={lockIconWarning}>
                          <EDIcon
                            iconType="lock_open_filled"
                            size="medium"
                            color="inherit"
                          />
                        </span>
                      </EDTooltip>
                    )}
                  </>
                )}
              </div>
            </EDTypography>
          </div>
        )}
        {this.props.isEditing && (
          <>
            {this.props.headerText && (
              <EDTypography
                style={{ display: 'inline' }}
                variant="h6"
                color="textPrimary"
                className={this.props.classes.helperText}
                bold
              >
                {this.props.headerText}
              </EDTypography>
            )}
            <div style={{ display: 'flex' }}>
              <EkonomiskAnalysPeriods
                dateInputType={this.props.dateInputType}
                isInterval={this.props.isInterval}
                dates={this.props.dates}
                onFromDateChange={this.props.onFromDateChange}
                onToDateChange={this.props.onToDateChange}
                color={this.props.lightVersion ? 'inherit' : undefined}
                dense={this.props.dense}
                classes={{
                  periodsContainer: this.props.classes.editModePeriods,
                }}
                padding={this.props.padding}
                fromDateRef={this.props.fromDateRef}
                toDateRef={this.props.toDateRef}
                updateReport={this.props.updateReport}
                toggleEditing={this.toggleEditing}
              />
              {!this.props.simple && (
                <EDIconButton
                  tooltip="Stäng"
                  icon="close"
                  iconSize="small"
                  padding="normal"
                  margin="none"
                  onClick={this.toggleEditing}
                  color="inherit"
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

export default withStyles(EkonomiskAnalysPeriodComponent, styles);
